import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  Button,
  ButtonBase,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Checkbox,
  Stack,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Collapse,
  Chip,
} from '@mui/material';
import Fade from '@mui/material/Fade';
import { alpha } from '@mui/material/styles';
import { rewriteToCdn } from '../../utils/cdn';
import {
  Visibility,
  Edit,
  Delete,
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  SwapVert as SwapVertIcon,
  Close as CloseIcon,
  CollectionsBookmark as CollectionsBookmarkIcon,
  InfoOutlined as InfoOutlinedIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import * as api from '../../services/api';
import { API_URL } from '../../config/api';
import { toast } from 'react-toastify';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useMemo } from 'react';
import PhotosSegmentedControl from '../shared/PhotosSegmentedControl';
import { userCardEnterSx } from '../shared/UserPageShell';
import { useI18n } from '../../i18n/useI18n';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { isStaffUser } from '../../utils/staffUser';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

const COL_ACCENT = '#e86a8a';
const COL_WARM = '#ff8ba7';

const dateFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2.5,
    bgcolor: alpha(COL_WARM, 0.04),
    '& fieldset': { borderColor: alpha(COL_WARM, 0.16) },
    '&:hover fieldset': { borderColor: alpha(COL_WARM, 0.35) },
    '&.Mui-focused fieldset': {
      borderColor: COL_ACCENT,
      borderWidth: 1.5,
    },
    '& input': {
      py: 1.15,
      fontSize: '0.875rem',
      color: COL_ACCENT,
    },
  },
} as const;

interface CollectionsProps {
  mode?: 'view' | 'select';
  selectedGroups?: string[];
  onClose?: () => void;
  initialGalleries?: string[];
  openCreateDialog?: boolean;
  userId?: string;
}

const Collections: React.FC<CollectionsProps> = ({ 
  mode = 'view', 
  selectedGroups = [], 
  onClose,
  initialGalleries = [],
  openCreateDialog = false,
  userId
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { pages: t } = useI18n();
  const { user } = useSelector((state: RootState) => state.auth);
  const readOnly = isStaffUser(user) && !userId;
  const [collections, setCollections] = useState<api.Collection[]>([]);
  const [filteredCollections, setFilteredCollections] = useState<api.Collection[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentCollection, setCurrentCollection] = useState({
    _id: '',
    title: '',
    description: '',
    headerImage: null as File | null,
    gifFrame: null as File | null,
    galleries: [] as string[],
    collectInfoEnabled: false
  });
  const [availableGalleries, setAvailableGalleries] = useState<api.PhotoGroupResponse[]>([]);
  const filteredGalleries = availableGalleries;
  const [headerImagePreview, setHeaderImagePreview] = useState('');
  // const [gifFramePreview, setGifFramePreview] = useState('');
  const [removeGifFrame, setRemoveGifFrame] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [collectionToDelete, setCollectionToDelete] = useState<api.Collection | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [galleryPage, setGalleryPage] = useState(1);
  const [hasMoreGalleries, setHasMoreGalleries] = useState(true);
  const [isLoadingGalleries, setIsLoadingGalleries] = useState(false);
  const limit = 10;
  const galleryLimit = 10; // Giảm từ 20 xuống 10 để giảm lag
  const [allCollectionGalleries, setAllCollectionGalleries] = useState<api.PhotoGroupResponse[]>([]); // Lưu tất cả galleries đã fetch từ collection
  const [collectionGalleriesDisplayCount, setCollectionGalleriesDisplayCount] = useState(10); // Số lượng galleries đang hiển thị
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [galleryStartDate, setGalleryStartDate] = useState('');
  const [galleryEndDate, setGalleryEndDate] = useState('');
  const [gallerySortOrder, setGallerySortOrder] = useState<'asc' | 'desc'>('desc');
  const [galleryFilterType, setGalleryFilterType] = useState<'all' | 'inCollection'>('all');
  const [isGalleryFilterExpanded, setIsGalleryFilterExpanded] = useState(false);
  const galleryEnterWave = `${galleryFilterType}|${galleryStartDate}|${galleryEndDate}|${gallerySortOrder}`;
  const [collectionCoverFallbacks, setCollectionCoverFallbacks] = useState<Record<string, string>>({});

  // Hàm xử lý URL ảnh để giảm kích thước và chất lượng (giống Photos.tsx)
  const getOptimizedImageUrl = (url: string, quality: number = 30) => {
    if (!url) return '/placeholder.png';
    
    const cdnUrl = rewriteToCdn(url);
    // Thêm tham số để giảm chất lượng ảnh
    const separator = cdnUrl.includes('?') ? '&' : '?';
    return `${cdnUrl}${separator}q=${quality}&w=200`; // giảm chất lượng xuống 30% và giới hạn width tối đa 200px cho thumbnail
  };

  // Chuẩn hóa URL ảnh gallery sang CDN ngay khi nhận về
  const normalizeGalleryUrls = (galleries: api.PhotoGroupResponse[]) =>
    galleries.map(gallery => ({
      ...gallery,
      photos: (gallery.photos || []).map(photo => ({
        ...photo,
        url: rewriteToCdn(photo.url),
      })),
    }));

  const pickCoverPhoto = (photos: api.PhotoGroupResponse['photos']) => {
    if (!photos || photos.length === 0) return undefined;
    return photos.find(photo => photo.name === '1.jpg')
      || photos.find(photo => photo.name === '1_cut2.jpg')
      || photos.find(photo => photo.name === '1_cut3.jpg')
      || photos[0];
  };

  const resolveCollectionFallbackCovers = async (items: api.Collection[]) => {
    const candidates = items.filter(
      (collection) =>
        !collection.headerImage?.url &&
        Array.isArray(collection.galleries) &&
        collection.galleries.length > 0
    );

    if (candidates.length === 0) return;

    const updates: Record<string, string> = {};
    await Promise.all(
      candidates.map(async (collection) => {
        try {
          const firstGalleryId = collection.galleries[0];
          if (!firstGalleryId) return;
          const gallery = await api.getPublicGalleryByFolderId(firstGalleryId);
          const cover = pickCoverPhoto(gallery.photos || []);
          if (cover?.url) {
            updates[collection._id] = rewriteToCdn(cover.url);
          }
        } catch (error) {
          // Silent fallback: nếu không lấy được ảnh đầu thì giữ ảnh mặc định
        }
      })
    );

    if (Object.keys(updates).length > 0) {
      setCollectionCoverFallbacks((prev) => ({ ...prev, ...updates }));
    }
  };

  const fetchCollections = async () => {
    try {
      const response = userId 
        ? await api.getAdminUserCollections(userId, page, limit)
        : await api.getCollections(page, limit);
      
      const newCollections = response.data;
      
      console.log(`[fetchCollections] Page ${page}: Received ${newCollections.length} items, totalPages: ${response.pagination.totalPages}, total: ${response.pagination.total}`);
      
      if (page === 1) {
        setCollections(newCollections);
      } else {
        setCollections(prev => [...prev, ...newCollections]);
      }
      resolveCollectionFallbackCovers(newCollections);
      // Kiểm tra xem còn data không: 
      // Nếu trả về đủ limit items VÀ chưa đến trang cuối thì còn data
      // Nếu không đủ limit items thì đã hết data
      const hasMoreData = newCollections.length === limit && page < response.pagination.totalPages;
      console.log(`[fetchCollections] Setting hasMore to ${hasMoreData} (items: ${newCollections.length}, limit: ${limit}, page: ${page}, totalPages: ${response.pagination.totalPages})`);
      setHasMore(hasMoreData);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching collections:', error);
      toast.error('Không thể tải danh sách collections');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let filtered = [...collections];

    // Filter by date
    if (startDate) {
      filtered = filtered.filter(collection => 
        new Date(collection.createdAt) >= new Date(startDate)
      );
    }

    if (endDate) {
      filtered = filtered.filter(collection => 
        new Date(collection.createdAt) <= new Date(endDate)
      );
    }

    // Sort by date
    filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

    setFilteredCollections(filtered);
  }, [collections, startDate, endDate, sortOrder]);

  // Hàm convert datetime-local sang ISO string (UTC)
  const convertToISO = (datetimeLocal: string): string => {
    if (!datetimeLocal) return '';
    // datetime-local format: "YYYY-MM-DDTHH:mm"
    // Convert sang ISO string với timezone local, sau đó convert sang UTC
    const localDate = new Date(datetimeLocal);
    // Lấy UTC time
    return localDate.toISOString();
  };

  // Fetch galleries từ collection IDs để lấy thumbnail (fetch một lần và lưu vào state)
  const fetchAllGalleriesFromCollectionIds = async () => {
    try {
      setIsLoadingGalleries(true);
      const collectionIds = currentCollection.galleries;
      if (collectionIds.length === 0) {
        setAllCollectionGalleries([]);
        setAvailableGalleries([]);
        setHasMoreGalleries(false);
        setIsLoadingGalleries(false);
        setCollectionGalleriesDisplayCount(10);
        return;
      }

      // Fetch tất cả galleries với limit lớn để lấy đủ dữ liệu
      const filter = galleryStartDate || galleryEndDate ? {
        startDate: galleryStartDate ? convertToISO(galleryStartDate) : undefined,
        endDate: galleryEndDate ? convertToISO(galleryEndDate) : undefined,
        sortOrder: gallerySortOrder
      } : { sortOrder: gallerySortOrder };

      let allGalleries: api.PhotoGroupResponse[] = [];
      let currentPage = 1;
      let hasMore = true;
      const fetchLimit = 500; // Dùng limit lớn để giảm số lần fetch

      // Fetch tất cả galleries
      while (hasMore) {
        let response;
        if (userId) {
          response = await api.getUserPhotos(userId, currentPage, fetchLimit);
        } else {
          response = await api.getPhotoGroups(currentPage, fetchLimit, filter);
        }

        const normalizedData = normalizeGalleryUrls(response.data);
        allGalleries.push(...normalizedData);

        hasMore = currentPage < response.pagination.totalPages;
        currentPage++;
      }

      // Filter chỉ lấy các gallery có ID trong collection, giữ nguyên thứ tự trong collection
      const filteredGalleries = collectionIds
        .map(folderId => allGalleries.find(gallery => gallery.folderId === folderId))
        .filter((gallery): gallery is api.PhotoGroupResponse => gallery !== undefined);

      // Lưu tất cả galleries vào state
      setAllCollectionGalleries(filteredGalleries);
      // Hiển thị batch đầu tiên (10 galleries)
      setCollectionGalleriesDisplayCount(10);
      setAvailableGalleries(filteredGalleries.slice(0, 10));
      setHasMoreGalleries(filteredGalleries.length > 10);
      setIsLoadingGalleries(false);
    } catch (error) {
      console.error('Error fetching galleries from collection:', error);
      toast.error('Không thể tải danh sách galleries');
      setIsLoadingGalleries(false);
    }
  };

  // Load thêm galleries từ collection (lazy load)
  const loadMoreCollectionGalleries = () => {
    const nextCount = collectionGalleriesDisplayCount + 10;
    setCollectionGalleriesDisplayCount(nextCount);
    setAvailableGalleries(allCollectionGalleries.slice(0, nextCount));
    setHasMoreGalleries(nextCount < allCollectionGalleries.length);
  };

  const fetchGalleries = async (pageNumber: number = 1) => {
    try {
      setIsLoadingGalleries(true);
      
      // Nếu chọn "Chỉ gallery trong collection", fetch từ collection IDs để lấy thumbnail
      if (galleryFilterType === 'inCollection') {
        await fetchAllGalleriesFromCollectionIds();
        return;
      }

      // Fetch bình thường với pagination
      const filter = galleryStartDate || galleryEndDate ? {
        startDate: galleryStartDate ? convertToISO(galleryStartDate) : undefined,
        endDate: galleryEndDate ? convertToISO(galleryEndDate) : undefined,
        sortOrder: gallerySortOrder
      } : undefined;

      if (userId) {
        // Khi admin xem user: getUserPhotos có pagination
        const response = await api.getUserPhotos(userId, pageNumber, galleryLimit);
        const normalizedData = normalizeGalleryUrls(response.data);
        
        if (pageNumber === 1) {
          setAvailableGalleries(normalizedData);
        } else {
          setAvailableGalleries(prev => [...prev, ...normalizedData]);
        }
        setHasMoreGalleries(pageNumber < response.pagination.totalPages);
      } else {
        // Khi user xem của chính họ: getPhotoGroups có pagination
        const response = await api.getPhotoGroups(pageNumber, galleryLimit, filter);
        const normalizedData = normalizeGalleryUrls(response.data);
        
        if (pageNumber === 1) {
          setAvailableGalleries(normalizedData);
        } else {
          setAvailableGalleries(prev => [...prev, ...normalizedData]);
        }
        setHasMoreGalleries(pageNumber < response.pagination.totalPages);
      }

      setIsLoadingGalleries(false);
    } catch (error) {
      console.error('Error fetching galleries:', error);
      toast.error('Không thể tải danh sách galleries');
      setIsLoadingGalleries(false);
    }
  };

  // Load galleries khi mở dialog hoặc đổi bộ lọc
  useEffect(() => {
    if (!openDialog) return;

    setIsLoadingGalleries(true);
    setAvailableGalleries([]);
    setAllCollectionGalleries([]);
    setGalleryPage(1);
    setCollectionGalleriesDisplayCount(10);
    setHasMoreGalleries(true);

    if (galleryFilterType === 'inCollection') {
      if (currentCollection.galleries.length > 0) {
        fetchAllGalleriesFromCollectionIds();
      } else {
        setHasMoreGalleries(false);
        setIsLoadingGalleries(false);
      }
      return;
    }

    fetchGalleries(1);
  }, [openDialog, galleryStartDate, galleryEndDate, gallerySortOrder, galleryFilterType]);

  // Add useEffect for gallery pagination
  useEffect(() => {
    if (openDialog && galleryPage > 1) {
      fetchGalleries(galleryPage);
    }
  }, [galleryPage, openDialog]);

  useEffect(() => {
    fetchCollections();
  }, [page]);

  const loadMore = () => {
    setPage(prev => prev + 1);
  };

  const loadMoreGalleries = () => {
    if (!isLoadingGalleries && hasMoreGalleries) {
      // Nếu đang ở chế độ "Chỉ gallery trong collection", load thêm từ state
      if (galleryFilterType === 'inCollection') {
        loadMoreCollectionGalleries();
      } else {
        // Load bình thường với pagination
        setGalleryPage(prev => prev + 1);
      }
    }
  };

  const handleCollectionClick = async (collection: api.Collection) => {
    if (mode === 'view') {
      window.open(`${API_URL}/collections/view/${collection._id}`, '_blank');
    } else if (mode === 'select' && selectedGroups) {
      try {
        setIsProcessing(true);
        // Kết hợp galleries hiện có với galleries mới được chọn
        const existingGalleries = collection.galleries || [];
        // Tạo mảng unique bằng cách filter
        const newGalleries = [...existingGalleries, ...selectedGroups].filter(
          (value, index, self) => self.indexOf(value) === index
        );

        const formData = new FormData();
        formData.append('title', collection.title);
        formData.append('description', collection.description || '');
        formData.append('galleries', JSON.stringify(newGalleries));

        if (userId) {
          await api.updateAdminUserCollection(userId, collection._id, formData);
        } else {
          await api.updateCollection(collection._id, formData);
        }

        toast.success('Đã thêm ảnh vào collection thành công');
        if (onClose) onClose();
      } catch (error) {
        console.error('Error updating collection:', error);
        toast.error('Không thể thêm ảnh vào collection');
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleEditClick = (e: React.MouseEvent, collection: api.Collection) => {
    e.stopPropagation();
    setIsEditMode(true);
    setCurrentCollection({
      _id: collection._id,
      title: collection.title,
      description: collection.description || '',
      headerImage: null,
      gifFrame: null,
      galleries: collection.galleries || [],
      collectInfoEnabled: collection.collectInfoEnabled ?? false
    });
    if (collection.headerImage?.url) {
      setHeaderImagePreview(rewriteToCdn(collection.headerImage.url));
    }
    // setGifFramePreview(collection.gifFrame?.url ? rewriteToCdn(collection.gifFrame.url) : '');
    setRemoveGifFrame(false);
    setIsGalleryFilterExpanded(false);
    setOpenDialog(true);
    setGalleryPage(1);
    // useEffect sẽ tự động fetch khi openDialog thay đổi
  };

  const handleDialogOpen = () => {
    setIsEditMode(false);
    setCurrentCollection({
      _id: '',
      title: '',
      description: '',
      headerImage: null,
      gifFrame: null,
      galleries: [],
      collectInfoEnabled: false
    });
    setHeaderImagePreview('');
    // setGifFramePreview('');
    setRemoveGifFrame(false);
    setIsGalleryFilterExpanded(false);
    setOpenDialog(true);
    setGalleryPage(1);
    // useEffect sẽ tự động fetch khi openDialog thay đổi
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setCurrentCollection({
      _id: '',
      title: '',
      description: '',
      headerImage: null,
      gifFrame: null,
      galleries: [],
      collectInfoEnabled: false
    });
    setHeaderImagePreview('');
    // setGifFramePreview('');
    setRemoveGifFrame(false);
  };

  const handleToggleCollectInfo = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    const collectionId = currentCollection._id;

    setCurrentCollection(prev => ({ ...prev, collectInfoEnabled: newValue }));

    // Chỉ gọi API khi đang chỉnh sửa (có _id). Khi tạo mới, sẽ lưu khi bấm Lưu
    if (!collectionId) return;

    try {
      if (userId) {
        await api.updateAdminUserCollectionCollectInfo(userId, collectionId, newValue);
      } else {
        await api.updateCollectionCollectInfo(collectionId, newValue);
      }
      toast.success(newValue ? 'Đã bật thu thập thông tin' : 'Đã tắt thu thập thông tin');
      setCollections(prev => prev.map(c =>
        c._id === collectionId ? { ...c, collectInfoEnabled: newValue } : c
      ));
    } catch (error: any) {
      setCurrentCollection(prev => ({ ...prev, collectInfoEnabled: !newValue }));
      toast.error('Không thể cập nhật. Vui lòng thử lại.');
    }
  };

  const handleSaveCollection = async () => {
    try {
      if (!currentCollection.title.trim()) {
        toast.error('Vui lòng nhập tiêu đề');
        return;
      }

      setIsProcessing(true);
      const formData = new FormData();
      formData.append('title', currentCollection.title);
      formData.append('description', currentCollection.description);
      formData.append('collectInfoEnabled', String(currentCollection.collectInfoEnabled));
      if (currentCollection.headerImage) {
        console.log('[Collections] Sending headerImage:', {
          name: (currentCollection.headerImage as File).name,
          type: (currentCollection.headerImage as File).type,
          size: (currentCollection.headerImage as File).size,
        });
        formData.append('headerImage', currentCollection.headerImage);
      } else {
        console.log('[Collections] No headerImage in currentCollection when saving.');
      }
      if (currentCollection.gifFrame) {
        formData.append('gifFrame', currentCollection.gifFrame);
      } else if (removeGifFrame) {
        formData.append('removeGifFrame', 'true');
      }
      formData.append('galleries', JSON.stringify(currentCollection.galleries));

      if (isEditMode) {
        if (userId) {
          await api.updateAdminUserCollection(userId, currentCollection._id, formData);
        } else {
          await api.updateCollection(currentCollection._id, formData);
        }
        toast.success('Collection đã được cập nhật');
      } else {
        if (userId) {
          await api.createAdminUserCollection(userId, formData);
        } else {
          await api.createCollection(formData);
        }
        toast.success('Collection đã được tạo');
      }

      handleDialogClose();
      setPage(1);
      fetchCollections();
    } catch (error) {
      console.error('Error saving collection:', error);
      toast.error('Không thể lưu collection');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleHeaderImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setCurrentCollection(prev => ({ ...prev, headerImage: file }));
      setHeaderImagePreview(URL.createObjectURL(file));
    }
  };

  /*
  const readImageSize = (file: File) =>
    new Promise<{ width: number; height: number }>((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const width = img.naturalWidth;
        const height = img.naturalHeight;
        URL.revokeObjectURL(url);
        resolve({ width, height });
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Không đọc được ảnh'));
      };
      img.src = url;
    });

  const handleGifFrameChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    try {
      const { width, height } = await readImageSize(file);
      if (width < 2 || height < 2 || Math.abs(width / height - 1.5) > 0.04) {
        toast.error('Khung GIF phải có tỉ lệ 3:2 (ngang), ví dụ 1800×1200');
        return;
      }
      setCurrentCollection((prev) => ({ ...prev, gifFrame: file }));
      setGifFramePreview(URL.createObjectURL(file));
      setRemoveGifFrame(false);
    } catch {
      toast.error('Không đọc được ảnh khung GIF');
    }
  };

  const handleClearGifFrame = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setCurrentCollection((prev) => ({ ...prev, gifFrame: null }));
    setGifFramePreview('');
    setRemoveGifFrame(true);
  };
  */

  const handleDeleteClick = (e: React.MouseEvent, collection: api.Collection) => {
    e.stopPropagation();
    setCollectionToDelete(collection);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCollectionToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!collectionToDelete) return;

    try {
      setIsProcessing(true);
      if (userId) {
        await api.deleteAdminUserCollection(userId, collectionToDelete._id);
      } else {
        await api.deleteCollection(collectionToDelete._id);
      }
      toast.success('Collection đã được xóa');
      setDeleteDialogOpen(false);
      setCollectionToDelete(null);
      setPage(1);
      fetchCollections();
    } catch (error) {
      console.error('Error deleting collection:', error);
      toast.error('Không thể xóa collection');
    } finally {
      setIsProcessing(false);
    }
  };

  const hasDateFilter = Boolean(startDate || endDate);
  const showDatePanel = isFilterExpanded;

  const clearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setIsFilterExpanded(false);
  };

  const handleSelectAllGalleries = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allGalleryIds = filteredGalleries.map(g => g.folderId);
      setCurrentCollection(prev => ({
        ...prev,
        galleries: allGalleryIds
      }));
    } else {
      setCurrentCollection(prev => ({
        ...prev,
        galleries: []
      }));
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: { xs: 1, md: 2 } }}>
      <Box
        sx={{
          mb: 2,
          p: { xs: 1.5, sm: 2 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: alpha(COL_WARM, 0.28),
          background: `linear-gradient(180deg, ${alpha('#fff', 0.98)} 0%, ${alpha(COL_WARM, 0.08)} 100%)`,
          boxShadow: `0 4px 18px ${alpha(COL_WARM, 0.08)}`,
        }}
      >
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          useFlexGap
          sx={{
            mb: showDatePanel ? 1.25 : 0,
            flexWrap: 'nowrap',
            gap: { xs: 0.5, sm: 0.75 },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flexShrink: 1, flex: mode === 'select' ? 1 : undefined }}>
            <SwapVertIcon sx={{ fontSize: 18, color: alpha(COL_ACCENT, 0.7), display: { xs: 'none', sm: 'block' } }} />
            <Box sx={{ minWidth: 0, width: mode === 'select' ? '100%' : 'auto', maxWidth: mode === 'select' ? '100%' : 'none' }}>
              <PhotosSegmentedControl
                value={sortOrder}
                onChange={(v) => setSortOrder(v)}
                size="sm"
                height={36}
                fullWidth={mode === 'select'}
                accentColor={COL_ACCENT}
                options={[
                  {
                    value: 'desc',
                    label: mode === 'select' ? t.common.newestShort : t.common.newest,
                  },
                  {
                    value: 'asc',
                    label: mode === 'select' ? t.common.oldestShort : t.common.oldest,
                  },
                ]}
              />
            </Box>
          </Box>

          <Box
            sx={{
              ml: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flexShrink: 0,
              height: 36,
            }}
          >
            <ButtonBase
              onClick={() => setIsFilterExpanded((prev) => !prev)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                height: 36,
                boxSizing: 'border-box',
                px: { xs: 1, sm: 1.25 },
                py: 0,
                borderRadius: 2,
                border: '1px solid',
                borderColor: showDatePanel || hasDateFilter
                  ? alpha(COL_ACCENT, 0.45)
                  : alpha(COL_WARM, 0.4),
                bgcolor: showDatePanel || hasDateFilter
                  ? alpha(COL_WARM, 0.28)
                  : alpha(COL_WARM, 0.14),
                color: COL_ACCENT,
              }}
            >
              <FilterListIcon sx={{ fontSize: 16 }} />
              <Typography
                component="span"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  whiteSpace: 'nowrap',
                  lineHeight: 1,
                }}
              >
                {t.photos.filterByDate}
              </Typography>
              {hasDateFilter && (
                <Chip
                  size="small"
                  label="ON"
                  sx={{
                    height: 16,
                    fontSize: '0.6rem',
                    fontWeight: 800,
                    bgcolor: COL_ACCENT,
                    color: '#fff',
                    '& .MuiChip-label': { px: 0.6 },
                  }}
                />
              )}
            </ButtonBase>

            {hasDateFilter && (
              <IconButton size="small" onClick={clearDateFilter} sx={{ color: COL_ACCENT, p: 0.5, height: 36, width: 36 }}>
                <ClearIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}

            {!readOnly && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
              onClick={handleDialogOpen}
              disabled={isProcessing}
              sx={{
                height: 36,
                minHeight: 36,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                whiteSpace: 'nowrap',
                lineHeight: 1,
                minWidth: { xs: 104, sm: 140 },
                px: { xs: 1.35, sm: 2 },
                bgcolor: COL_ACCENT,
                boxShadow: `0 4px 14px ${alpha(COL_ACCENT, 0.28)}`,
                '&:hover': { bgcolor: alpha(COL_ACCENT, 0.88) },
                '& .MuiButton-startIcon': { mr: { xs: 0.5, sm: 0.75 }, ml: 0 },
              }}
            >
              Tạo mới
            </Button>
            )}
          </Box>
        </Stack>

        <Collapse in={showDatePanel} timeout={220}>
          <Box
            sx={{
              mt: 0.5,
              p: { xs: 1.25, sm: 1.5 },
              borderRadius: 2.5,
              border: '1px solid',
              borderColor: alpha(COL_WARM, 0.2),
              background: `linear-gradient(180deg, ${alpha('#fff', 0.95)} 0%, ${alpha(COL_WARM, 0.06)} 100%)`,
            }}
          >
            <Grid container spacing={1.25}>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mb: 0.5,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: alpha(COL_ACCENT, 0.85),
                    fontSize: '0.68rem',
                  }}
                >
                  {t.common.fromDate}
                </Typography>
                <TextField
                  fullWidth
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={dateFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mb: 0.5,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: alpha(COL_ACCENT, 0.85),
                    fontSize: '0.68rem',
                  }}
                >
                  {t.common.toDate}
                </Typography>
                <TextField
                  fullWidth
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={dateFieldSx}
                />
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Box>

      <InfiniteScroll
        dataLength={filteredCollections.length}
        next={loadMore}
        hasMore={hasMore}
        loader={
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress />
          </Box>
        }
      >
        <Grid container spacing={{ xs: 1.25, sm: 2 }}>
          {filteredCollections.map((collection, index) => (
            <Grid item xs={6} sm={6} md={4} key={collection._id}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: alpha(COL_WARM, 0.35),
                  background: `linear-gradient(180deg, ${alpha('#fff', 0.98)} 0%, ${alpha(COL_WARM, 0.12)} 100%)`,
                  boxShadow: `0 4px 16px ${alpha(COL_WARM, 0.1)}`,
                  ...userCardEnterSx(index),
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 24px ${alpha(COL_WARM, 0.22)}`,
                  },
                }}
                onClick={() => handleCollectionClick(collection)}
              >
                <CardMedia
                  component="img"
                  sx={{
                    height: { xs: 110, sm: 140 },
                    objectFit: 'cover',
                    bgcolor: alpha(COL_WARM, 0.1),
                  }}
                  image={
                    collection.headerImage?.url
                      ? rewriteToCdn(collection.headerImage.url)
                      : (collectionCoverFallbacks[collection._id] || '/default-collection-image.jpg')
                  }
                  alt={collection.title}
                />
                <CardContent sx={{ flexGrow: 1, px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Typography
                      variant="subtitle1"
                      noWrap
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontWeight: 700,
                        color: COL_ACCENT,
                        fontSize: { xs: '0.8rem', sm: '1rem' },
                      }}
                    >
                      {collection.title}
                    </Typography>
                    {collection.collectInfoEnabled && (
                      <Typography
                        variant="caption"
                        sx={{
                          px: 0.75,
                          py: 0.2,
                          borderRadius: 1,
                          bgcolor: alpha(COL_ACCENT, 0.85),
                          color: '#fff',
                          whiteSpace: 'nowrap',
                          fontSize: '0.6rem',
                        }}
                      >
                        Thu thập thông tin
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ color: alpha(COL_ACCENT, 0.7), fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                  >
                    {collection.description || 'Không có mô tả'}
                  </Typography>
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ color: alpha(COL_ACCENT, 0.65), fontSize: { xs: '0.62rem', sm: '0.75rem' }, mt: 0.25 }}
                  >
                    Tạo lúc: {new Date(collection.createdAt).toLocaleString()}
                  </Typography>
                </CardContent>
                {!readOnly && (
                <CardActions sx={{ pt: 0, px: { xs: 0.5, sm: 1 } }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick(e, collection);
                    }}
                    disabled={isProcessing}
                    sx={{ color: COL_ACCENT }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(e, collection);
                    }}
                    disabled={isProcessing}
                    sx={{ color: COL_ACCENT }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </CardActions>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      </InfiniteScroll>

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        TransitionComponent={Fade}
        transitionDuration={{ enter: 0, exit: 180 }}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            overflow: 'hidden',
            height: isMobile ? '100%' : 'min(96vh, 980px)',
            maxHeight: isMobile ? '100%' : 'min(96vh, 980px)',
            m: isMobile ? 0 : 1.5,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#fffafb',
            backgroundImage: `linear-gradient(180deg, ${alpha(COL_WARM, 0.14)} 0%, #fff 18%)`,
            animation: 'dashCardIn 0.45s ease both',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: { xs: 1.5, sm: 2.5 },
            py: { xs: 1, sm: 1.25 },
            borderBottom: '1px solid',
            borderColor: alpha(COL_ACCENT, 0.12),
            bgcolor: alpha('#fff', 0.88),
            backdropFilter: 'blur(8px)',
            flexShrink: 0,
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(COL_ACCENT, 0.12),
              color: COL_ACCENT,
              flexShrink: 0,
            }}
          >
            <CollectionsBookmarkIcon fontSize="small" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={800} sx={{ color: COL_ACCENT, lineHeight: 1.2 }}>
              {isEditMode ? 'Chỉnh sửa bộ sưu tập' : 'Tạo bộ sưu tập mới'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {currentCollection.galleries.length} gallery đã chọn
            </Typography>
          </Box>
          <IconButton onClick={handleDialogClose} size="small" aria-label="Đóng" sx={{ color: COL_ACCENT }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            px: { xs: 1.25, sm: 2 },
            py: { xs: 1, sm: 1.25 },
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr) 96px', sm: 'minmax(0, 1fr) 168px' },
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Stack spacing={1}>
              <TextField
                fullWidth
                label="Tiêu đề"
                value={currentCollection.title}
                onChange={(e) => setCurrentCollection((prev) => ({ ...prev, title: e.target.value }))}
                required
                size="small"
                sx={dateFieldSx}
              />
              <TextField
                fullWidth
                label="Mô tả"
                value={currentCollection.description}
                onChange={(e) => setCurrentCollection((prev) => ({ ...prev, description: e.target.value }))}
                multiline
                minRows={1}
                maxRows={2}
                size="small"
                sx={dateFieldSx}
              />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: alpha(COL_ACCENT, 0.16),
                  bgcolor: alpha(COL_WARM, 0.06),
                }}
              >
                <InfoOutlinedIcon sx={{ color: COL_ACCENT, fontSize: 18, flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" fontWeight={700} sx={{ color: COL_ACCENT, display: 'block', lineHeight: 1.2 }}>
                    Thu thập thông tin khách
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: { xs: 'none', sm: 'block' } }}>
                    Khách xem gallery sẽ nhập email và tên
                  </Typography>
                </Box>
                <Switch
                  size="small"
                  checked={currentCollection.collectInfoEnabled}
                  onChange={handleToggleCollectInfo}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: COL_ACCENT },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: COL_ACCENT },
                  }}
                />
              </Box>
            </Stack>

            <Box>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="header-image-upload"
                type="file"
                onChange={handleHeaderImageChange}
              />
              <Box
                component="label"
                htmlFor="header-image-upload"
                sx={{
                  display: 'block',
                  cursor: 'pointer',
                  borderRadius: 2,
                  border: '1.5px dashed',
                  borderColor: alpha(COL_ACCENT, 0.35),
                  bgcolor: alpha(COL_WARM, 0.05),
                  overflow: 'hidden',
                  height: '100%',
                  minHeight: { xs: 108, sm: 118 },
                  '&:active': { opacity: 0.92 },
                }}
              >
                {headerImagePreview ? (
                  <Box sx={{ position: 'relative', height: '100%' }}>
                    <Box
                      component="img"
                      src={headerImagePreview}
                      alt="Header preview"
                      sx={{
                        display: 'block',
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <Chip
                      icon={<CloudUploadIcon sx={{ fontSize: '15px !important' }} />}
                      label="Đổi ảnh bìa"
                      size="small"
                      sx={{
                        position: 'absolute',
                        right: 6,
                        bottom: 6,
                        height: 24,
                        bgcolor: 'rgba(255,255,255,0.92)',
                        color: COL_ACCENT,
                        fontWeight: 700,
                        '& .MuiChip-icon': { color: COL_ACCENT },
                      }}
                    />
                  </Box>
                ) : (
                  <Stack alignItems="center" justifyContent="center" spacing={0.35} sx={{ height: '100%', px: 1 }}>
                    <CloudUploadIcon sx={{ color: COL_ACCENT, fontSize: 22 }} />
                    <Typography variant="caption" fontWeight={700} sx={{ color: COL_ACCENT, textAlign: 'center' }}>
                      {isEditMode ? 'Đổi ảnh bìa' : 'Tải ảnh bìa'}
                    </Typography>
                  </Stack>
                )}
              </Box>
            </Box>
          </Box>

          {/* Khung GIF — tạm ẩn, bật lại khi cần overlay GIF.
          <Box sx={{ flexShrink: 0 }}>
            <input
              accept="image/png,image/jpeg,image/webp"
              style={{ display: 'none' }}
              id="gif-frame-upload"
              type="file"
              onChange={handleGifFrameChange}
            />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 220px' },
                gap: 1,
                alignItems: 'center',
                px: 1.25,
                py: 1,
                borderRadius: 2,
                border: '1px solid',
                borderColor: alpha(COL_ACCENT, 0.16),
                bgcolor: alpha(COL_WARM, 0.04),
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" fontWeight={700} sx={{ color: COL_ACCENT, display: 'block', lineHeight: 1.2 }}>
                  Khung GIF
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Ảnh overlay lên GIF trong gallery. Bắt buộc tỉ lệ 3×2 ngang (ví dụ 1800×1200).
                </Typography>
              </Box>
              <Box
                component="label"
                htmlFor="gif-frame-upload"
                sx={{
                  display: 'block',
                  cursor: 'pointer',
                  borderRadius: 1.5,
                  border: '1.5px dashed',
                  borderColor: alpha(COL_ACCENT, 0.35),
                  bgcolor: '#fff',
                  overflow: 'hidden',
                  aspectRatio: '3 / 2',
                  width: '100%',
                  '&:active': { opacity: 0.92 },
                }}
              >
                {gifFramePreview ? (
                  <Box sx={{ position: 'relative', height: '100%' }}>
                    <Box
                      component="img"
                      src={gifFramePreview}
                      alt="GIF frame preview"
                      sx={{
                        display: 'block',
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <Chip
                      icon={<CloudUploadIcon sx={{ fontSize: '15px !important' }} />}
                      label="Đổi khung"
                      size="small"
                      sx={{
                        position: 'absolute',
                        left: 6,
                        bottom: 6,
                        height: 24,
                        bgcolor: 'rgba(255,255,255,0.92)',
                        color: COL_ACCENT,
                        fontWeight: 700,
                        '& .MuiChip-icon': { color: COL_ACCENT },
                      }}
                    />
                    <IconButton
                      size="small"
                      aria-label="Xóa khung GIF"
                      onClick={handleClearGifFrame}
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        width: 26,
                        height: 26,
                        bgcolor: 'rgba(255,255,255,0.92)',
                        '&:hover': { bgcolor: '#fff' },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 16, color: COL_ACCENT }} />
                    </IconButton>
                  </Box>
                ) : (
                  <Stack alignItems="center" justifyContent="center" spacing={0.25} sx={{ height: '100%', px: 1 }}>
                    <CloudUploadIcon sx={{ color: COL_ACCENT, fontSize: 20 }} />
                    <Typography variant="caption" fontWeight={700} sx={{ color: COL_ACCENT, textAlign: 'center' }}>
                      Tải khung GIF 3:2
                    </Typography>
                  </Stack>
                )}
              </Box>
            </Box>
          </Box>
          */}

          <Box
            sx={{
              borderRadius: 2.5,
              border: '1px solid',
              borderColor: alpha(COL_ACCENT, 0.14),
              bgcolor: '#fff',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                px: { xs: 1.25, sm: 1.75 },
                pt: 1,
                pb: 0.75,
                borderBottom: '1px solid',
                borderColor: alpha(COL_ACCENT, 0.1),
                flexShrink: 0,
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={{ xs: 0, sm: 1 }} gap={1}>
                <Typography variant="subtitle2" fontWeight={800} sx={{ color: COL_ACCENT }}>
                  Chọn Galleries
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
                  <Chip
                    size="small"
                    label={`${currentCollection.galleries.length} đã chọn`}
                    sx={{
                      height: 24,
                      fontWeight: 700,
                      bgcolor: alpha(COL_ACCENT, 0.1),
                      color: COL_ACCENT,
                    }}
                  />
                  <Chip
                    size="small"
                    clickable
                    icon={<FilterListIcon sx={{ fontSize: '16px !important' }} />}
                    label={
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                        {isGalleryFilterExpanded ? 'Ẩn lọc' : 'Lọc'}
                        <ExpandMoreIcon
                          sx={{
                            fontSize: 18,
                            transform: isGalleryFilterExpanded ? 'rotate(180deg)' : 'none',
                            transition: 'transform .2s',
                          }}
                        />
                      </Box>
                    }
                    onClick={() => setIsGalleryFilterExpanded((v) => !v)}
                    sx={{
                      display: { xs: 'inline-flex', sm: 'none' },
                      height: 24,
                      fontWeight: 700,
                      bgcolor: isGalleryFilterExpanded || galleryStartDate || galleryEndDate || galleryFilterType !== 'all'
                        ? alpha(COL_ACCENT, 0.16)
                        : alpha(COL_ACCENT, 0.08),
                      color: COL_ACCENT,
                      '& .MuiChip-icon': { color: COL_ACCENT },
                    }}
                  />
                </Stack>
              </Stack>

              <Collapse in={!isMobile || isGalleryFilterExpanded} timeout="auto" unmountOnExit={false}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr 1fr',
                    sm: 'minmax(140px, 1fr) minmax(140px, 1fr) minmax(140px, 1fr) minmax(110px, 0.8fr)',
                  },
                  gap: 1,
                  mt: { xs: 1, sm: 0 },
                }}
              >
                <FormControl size="small" fullWidth>
                  <InputLabel>Hiển thị</InputLabel>
                  <Select
                    value={galleryFilterType}
                    label="Hiển thị"
                    onChange={(e) => setGalleryFilterType(e.target.value as 'all' | 'inCollection')}
                  >
                    <MenuItem value="all">Tất cả</MenuItem>
                    <MenuItem value="inCollection">Trong BST</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Sắp xếp</InputLabel>
                  <Select
                    value={gallerySortOrder}
                    label="Sắp xếp"
                    onChange={(e) => setGallerySortOrder(e.target.value as 'asc' | 'desc')}
                  >
                    <MenuItem value="desc">Mới nhất</MenuItem>
                    <MenuItem value="asc">Cũ nhất</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  type="datetime-local"
                  label="Từ ngày"
                  value={galleryStartDate}
                  onChange={(e) => setGalleryStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  fullWidth
                />
                <TextField
                  type="datetime-local"
                  label="Đến ngày"
                  value={galleryEndDate}
                  onChange={(e) => setGalleryEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  fullWidth
                />
              </Box>
              </Collapse>

              <FormControlLabel
                sx={{ mt: 0.75, ml: -0.5, '& .MuiFormControlLabel-label': { fontSize: '0.85rem', fontWeight: 600 } }}
                control={
                  <Checkbox
                    size="small"
                    checked={
                      filteredGalleries.length > 0 &&
                      currentCollection.galleries.length === filteredGalleries.length
                    }
                    onChange={handleSelectAllGalleries}
                    indeterminate={
                      currentCollection.galleries.length > 0 &&
                      currentCollection.galleries.length < filteredGalleries.length
                    }
                    sx={{
                      color: alpha(COL_ACCENT, 0.45),
                      '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: COL_ACCENT },
                    }}
                  />
                }
                label="Chọn tất cả"
              />
            </Box>

            <Box
              id="gallery-list"
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                px: { xs: 1, sm: 1.25 },
                py: 1,
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <InfiniteScroll
                dataLength={filteredGalleries.length}
                next={loadMoreGalleries}
                hasMore={hasMoreGalleries && filteredGalleries.length > 0}
                loader={
                  <Box display="flex" justifyContent="center" p={1.5}>
                    <CircularProgress size={22} sx={{ color: COL_ACCENT }} />
                  </Box>
                }
                scrollableTarget="gallery-list"
                style={{ overflow: 'visible' }}
              >
                {isLoadingGalleries && filteredGalleries.length === 0 ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    gap={1.25}
                    sx={{ minHeight: { xs: 180, sm: 240 }, py: 4 }}
                  >
                    <CircularProgress size={28} sx={{ color: COL_ACCENT }} />
                    <Typography variant="body2" color="text.secondary">
                      Đang tải gallery...
                    </Typography>
                  </Box>
                ) : (
                  <>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'repeat(2, minmax(0, 1fr))',
                      sm: 'repeat(3, minmax(0, 1fr))',
                      md: 'repeat(4, minmax(0, 1fr))',
                    },
                    gap: { xs: 0.85, sm: 1 },
                  }}
                >
                  {filteredGalleries.map((gallery, index) => {
                    const isSelected = currentCollection.galleries.includes(gallery.folderId);
                    const coverPhoto =
                      gallery.photos.find((photo) => photo.name === '1.jpg') ||
                      gallery.photos.find((photo) => photo.name === '1_cut2.jpg') ||
                      gallery.photos.find((photo) => photo.name === '1_cut3.jpg') ||
                      (gallery.photos.length > 0 ? gallery.photos[0] : undefined);

                    return (
                      <Box
                        key={`${galleryEnterWave}-${gallery.folderId}`}
                        onClick={() => {
                          const newGalleries = isSelected
                            ? currentCollection.galleries.filter((id) => id !== gallery.folderId)
                            : [...currentCollection.galleries, gallery.folderId];
                          setCurrentCollection((prev) => ({
                            ...prev,
                            galleries: newGalleries,
                          }));
                        }}
                        sx={{
                          position: 'relative',
                          borderRadius: 2,
                          overflow: 'hidden',
                          border: '2px solid',
                          borderColor: isSelected ? COL_ACCENT : alpha(COL_ACCENT, 0.12),
                          bgcolor: '#fff',
                          cursor: 'pointer',
                          boxShadow: isSelected ? `0 0 0 2px ${alpha(COL_ACCENT, 0.2)}` : 'none',
                          transition: 'border-color .15s, box-shadow .15s, opacity .15s',
                          animation: 'dashCardIn 0.38s ease both',
                          animationDelay: `${(index % 12) * 0.028}s`,
                          '&:active': { opacity: 0.92 },
                          '@media (prefers-reduced-motion: reduce)': {
                            animation: 'none',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            pt: '64%',
                            bgcolor: alpha(COL_WARM, 0.08),
                          }}
                        >
                          {coverPhoto ? (
                            <Box
                              component="img"
                              src={getOptimizedImageUrl(coverPhoto.url)}
                              alt={gallery.folderId}
                              loading="lazy"
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'grid',
                                placeItems: 'center',
                                color: alpha(COL_ACCENT, 0.4),
                                fontWeight: 800,
                              }}
                            >
                              G
                            </Box>
                          )}
                          <Checkbox
                            checked={isSelected}
                            size="small"
                            tabIndex={-1}
                            disableRipple
                            sx={{
                              position: 'absolute',
                              top: 2,
                              left: 2,
                              p: 0.5,
                              bgcolor: 'rgba(255,255,255,0.88)',
                              borderRadius: 1,
                              color: alpha(COL_ACCENT, 0.5),
                              '&.Mui-checked': { color: COL_ACCENT },
                            }}
                          />
                        </Box>
                        <Box sx={{ px: 0.85, py: 0.75 }}>
                          <Typography
                            variant="caption"
                            noWrap
                            title={gallery.folderId}
                            sx={{
                              display: 'block',
                              fontWeight: 700,
                              fontSize: '0.68rem',
                              fontFamily: 'ui-monospace, monospace',
                              color: COL_ACCENT,
                            }}
                          >
                            {gallery.folderId}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                            {gallery.photos.length} ảnh · {new Date(gallery.createdAt).toLocaleDateString('vi-VN')}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
                {!isLoadingGalleries && !filteredGalleries.length && (
                  <Box py={4} textAlign="center">
                    <Typography variant="body2" color="text.secondary">
                      Không có gallery phù hợp
                    </Typography>
                  </Box>
                )}
                  </>
                )}
              </InfiniteScroll>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 1.5, sm: 2.5 },
            py: { xs: 1, sm: 1.15 },
            gap: 1,
            borderTop: '1px solid',
            borderColor: alpha(COL_ACCENT, 0.12),
            bgcolor: alpha('#fff', 0.95),
            flexShrink: 0,
            zIndex: 2,
          }}
        >
          <Button
            onClick={handleDialogClose}
            disabled={isProcessing}
            sx={{ color: COL_ACCENT, fontWeight: 700, textTransform: 'none' }}
          >
            Hủy
          </Button>
          <Button
            onClick={handleSaveCollection}
            variant="contained"
            disabled={isProcessing}
            sx={{
              ml: '0 !important',
              px: 2.5,
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: COL_ACCENT,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#d45a78', boxShadow: 'none' },
            }}
          >
            {isProcessing ? 'Đang lưu...' : 'Lưu'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Xác nhận xóa</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bạn có chắc chắn muốn xóa collection "{collectionToDelete?.title}" không?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={isProcessing}>
            Hủy
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={isProcessing}>
            {isProcessing ? 'Đang xử lý...' : 'Xóa'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Collections; 